/**
 * User Access Cookie Management
 * Handles storing and retrieving user information for white paper access
 * with time-based expiration (10 hours)
 */

export interface UserAccessData {
  name: string
  email: string
  organization: string
  phone?: string
  timestamp: number // Unix timestamp when data was saved
}

const COOKIE_NAME = 'eceptionist_user_access'
const EXPIRATION_HOURS = 10
const EXPIRATION_MS = EXPIRATION_HOURS * 60 * 60 * 1000 // Convert hours to milliseconds

// Cookie security attributes.
// NOTE: HttpOnly cannot be applied from client-side JS — only servers can set it.
// `Secure` is applied only on HTTPS contexts so local HTTP dev still works
// (browsers reject Secure cookies on http:// origins).
function getCookieSecurityAttributes(): string {
  const isHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:'
  const secureFlag = isHttps ? '; Secure' : ''
  return `; path=/; SameSite=Strict${secureFlag}`
}

/**
 * Check if user access data is valid (not expired)
 */
export function isUserAccessValid(): boolean {
  const data = getUserAccessData()
  if (!data) return false
  
  const now = Date.now()
  const isExpired = (now - data.timestamp) > EXPIRATION_MS
  
  if (isExpired) {
    // Clean up expired data
    clearUserAccessData()
    return false
  }
  
  return true
}

/**
 * Get user access data from cookie
 */
export function getUserAccessData(): UserAccessData | null {
  try {
    const cookieValue = document.cookie
      .split('; ')
      .find(row => row.startsWith(`${COOKIE_NAME}=`))
      ?.split('=')[1]
    
    if (!cookieValue) return null
    
    const decoded = decodeURIComponent(cookieValue)
    const data = JSON.parse(decoded) as UserAccessData
    
    // Validate the data structure
    if (!data.name || !data.email || !data.organization || !data.timestamp) {
      return null
    }
    
    return data
  } catch (error) {
    console.warn('Failed to parse user access cookie:', error)
    return null
  }
}

/**
 * Save user access data to cookie
 */
export function saveUserAccessData(userData: Omit<UserAccessData, 'timestamp'>): void {
  try {
    const data: UserAccessData = {
      ...userData,
      timestamp: Date.now()
    }
    
    const encoded = encodeURIComponent(JSON.stringify(data))
    const expirationDate = new Date(Date.now() + EXPIRATION_MS)

    document.cookie = `${COOKIE_NAME}=${encoded}; expires=${expirationDate.toUTCString()}${getCookieSecurityAttributes()}`
  } catch (error) {
    console.error('Failed to save user access data:', error)
  }
}

/**
 * Clear user access data from cookie
 */
export function clearUserAccessData(): void {
  // Must match the attributes used when setting the cookie for reliable deletion across browsers.
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC${getCookieSecurityAttributes()}`
}

/**
 * Get user access data if valid, otherwise return null
 */
export function getValidUserAccessData(): UserAccessData | null {
  return isUserAccessValid() ? getUserAccessData() : null
}

/**
 * Get remaining time until expiration in hours
 */
export function getRemainingAccessTime(): number {
  const data = getUserAccessData()
  if (!data) return 0
  
  const now = Date.now()
  const remainingMs = EXPIRATION_MS - (now - data.timestamp)
  
  return Math.max(0, remainingMs / (60 * 60 * 1000)) // Convert to hours
}
